import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import * as QuillCore from 'quill';
import { Delta, Op } from 'quill';

import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';

export interface RichEditorRef {
  getHtml: () => string;
  getDelta: () => Op[];
}

interface RichEditorProps {
  defaultValue?: Delta;
  maxLength?: number;
  placeholder?: string;
}

const RichEditor = forwardRef<RichEditorRef, RichEditorProps>(
  ({ defaultValue, maxLength = 5000, placeholder = '' }, ref) => {
    const quillRef = useRef<QuillCore.default | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const defaultValueRef = useRef<Delta | undefined>(defaultValue);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      const editorContainer = container.appendChild(
        container.ownerDocument.createElement('div')
      );

      const quill = new QuillCore.default(editorContainer, {
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [
              'bold',
              'italic',
              'underline',
              'strike',
              'link',
              { color: [] },
              { background: [] },
            ],
            [{ align: [] }, { list: 'ordered' }, { list: 'bullet' }],
            ['clean'],
          ],
        },
        placeholder,
        theme: 'snow',
      });

      quillRef.current = quill;

      if (defaultValueRef.current) {
        quill.setContents(defaultValueRef.current);
      }

      // 监听文本变化, 限制最大长度
      quill.on('text-change', () => {
        const delta = quill.getContents();
        const length = delta.reduce(
          (sum: number, op: Op) =>
            sum + (op.insert ? (op.insert as string).length : 0),
          0
        );
        if (length > maxLength) {
          quill.deleteText(maxLength, length - maxLength);
        }
      });

      return () => {
        container.innerHTML = '';
        quillRef.current = null;
        quill.off('text-change');
      };
    }, [defaultValue, maxLength]);

    useImperativeHandle(ref, () => ({
      getHtml: () => quillRef.current?.getSemanticHTML() || '',
      getDelta: () => quillRef.current?.getContents().ops || [],
    }));

    return <div ref={containerRef}></div>;
  }
);

RichEditor.displayName = 'RichEditor';

export default RichEditor;
